/**
 * Reports from what was recorded, defined as pack data and computed on the device: the MoH
 * registers (line lists to print or export), and the measures a dashboard shows (counts and
 * percentages over a period, monthly trends).
 *
 * A row is one filled form (an Encounter) with its values by concept code, plus who it is for:
 *   name, mrn, kebele, woreda, phone, sex, dob, age (whole years at the row's date), age_days,
 *   date (YYYY-MM-DD), form, shown (the rule ids the visit showed, comma-separated: has(shown, 'X')),
 *   referred (a referral was made at this visit), next_visit (the return date set at this visit).
 * A register can add a person's latest value of any concept as p_<code>, and rows of a delivery
 * can be one per newborn (the baby's own record, linked to her mother).
 */
import { conceptOf, daysBetween, observationValue } from './context.js';
import { evaluate, parse } from './expr.js';
import { EXT_CDS_PROVENANCE, SYS_FACILITY_MRN, } from './fhir.js';
import { isLabOrder, urgencyOf } from './referral.js';
import { formOfEncounter, motherOf, nextVisitOf } from './visit.js';
const astCache = new Map();
const ev = (expr, ctx) => {
    let ast = astCache.get(expr);
    if (!ast) {
        ast = parse(expr);
        astCache.set(expr, ast);
    }
    return evaluate(ast, ctx);
};
const usable = (o) => o.status !== 'entered-in-error' && o.status !== 'cancelled';
function ageAt(p, iso) {
    const b = p.birthDate;
    if (!b)
        return { years: null, days: null };
    const [ty, tm, td] = iso.split('-').map(Number);
    const y = Number(b.slice(0, 4));
    if (b.length < 10)
        return { years: ty - y, days: null };
    const m = Number(b.slice(5, 7));
    const d = Number(b.slice(8, 10));
    return {
        years: ty - y - (tm < m || (tm === m && td < d) ? 1 : 0),
        days: daysBetween(b, iso),
    };
}
/** Who the row is for (the person columns every register starts with). */
export function personValues(p, iso) {
    const a = ageAt(p, iso);
    return {
        name: p.name?.[0]?.text || (p.name?.[0]?.given ?? []).join(' ') || null,
        mrn: p.identifier?.find((i) => i.system === SYS_FACILITY_MRN)?.value ?? null,
        kebele: (p.address?.[0]?.line?.[0] ?? '').replace(/^Kebele\s+/, '') || null,
        woreda: p.address?.[0]?.district ?? null,
        phone: p.telecom?.find((t) => t.system === 'phone')?.value ?? null,
        sex: p.gender ?? null,
        dob: p.birthDate ?? null,
        age: a.years,
        age_days: a.days,
    };
}
const shownOf = (e) => {
    const ext = (e.extension ?? []).find((x) => x.url === EXT_CDS_PROVENANCE);
    const ids = (ext?.extension ?? [])
        .filter((x) => x.url === 'shown' && x.valueString)
        .map((x) => x.valueString.split('@')[0]);
    return ids.length ? ids.join(',') : null;
};
/** One row per filled form of these kinds (all dates; filter with inPeriod). */
export function formRows(bundles, forms, opts = {}) {
    const want = new Set(forms);
    const out = [];
    for (const b of bundles) {
        const encs = b.record.encounters.filter((e) => e.status !== 'entered-in-error' && want.has(formOfEncounter(e) ?? ''));
        if (!encs.length)
            continue;
        const byEnc = new Map();
        for (const o of b.record.observations) {
            const r = o.encounter?.reference;
            if (r && usable(o))
                byEnc.set(r, [...(byEnc.get(r) ?? []), o]);
        }
        const person = {};
        for (const code of opts.person ?? []) {
            const last = b.record.observations
                .filter((o) => usable(o) && conceptOf(o) === code)
                .sort((x, y) => y.effectiveDateTime.localeCompare(x.effectiveDateTime))[0];
            person[`p_${code}`] = last ? observationValue(last) : null;
        }
        for (const form of opts.first ?? []) {
            const dates = b.record.encounters
                .filter((e) => e.status !== 'entered-in-error' && formOfEncounter(e) === form)
                .map((e) => e.period.start.slice(0, 10))
                .sort();
            person[`first_${form}`] = dates[0] ?? null;
        }
        const referredAt = new Set(b.record.referrals.filter((s) => !isLabOrder(s)).map((s) => s.encounter?.reference));
        for (const e of encs) {
            const values = {};
            for (const o of byEnc.get(`Encounter/${e.id}`) ?? []) {
                const c = conceptOf(o);
                if (c)
                    values[c] = observationValue(o);
            }
            const own = opts.date ? values[opts.date] : null;
            const date = typeof own === 'string' && own.length >= 10
                ? own.slice(0, 10)
                : e.period.start.slice(0, 10);
            out.push({
                patient: b.patient,
                encounter: e,
                date,
                values: {
                    ...personValues(b.patient, date),
                    ...person,
                    ...values,
                    date,
                    form: formOfEncounter(e),
                    shown: shownOf(e),
                    referred: referredAt.has(`Encounter/${e.id}`),
                    next_visit: nextVisitOf(e),
                },
            });
        }
    }
    return out.sort((a, b) => a.date.localeCompare(b.date));
}
/** Rows of referrals made (not laboratory requests): urgency, has_outcome, status, reason. */
export function referralRows(bundles) {
    const out = [];
    for (const b of bundles)
        for (const s of b.record.referrals) {
            if (isLabOrder(s) || s.status === 'entered-in-error')
                continue;
            const date = (s.authoredOn ?? '').slice(0, 10);
            out.push({
                patient: b.patient,
                encounter: null,
                date,
                values: {
                    ...personValues(b.patient, date),
                    date,
                    urgency: urgencyOf(s),
                    status: s.status,
                    has_outcome: s.status !== 'active',
                    reason: s.reasonCode?.[0]?.text ?? null,
                },
            });
        }
    return out;
}
/** Rows of enrolments in a programme (the day each episode began). */
export function enrolmentRows(bundles, programme) {
    const out = [];
    for (const b of bundles)
        for (const e of b.record.episodes) {
            if (e.status === 'entered-in-error')
                continue;
            if (!e.type.some((t) => t.coding?.some((c) => c.code === programme)))
                continue;
            const date = (e.period?.start ?? '').slice(0, 10);
            out.push({
                patient: b.patient,
                encounter: null,
                date,
                values: { ...personValues(b.patient, date), date, open: e.status === 'active' },
            });
        }
    return out;
}
export const inPeriod = (rows, from, to) => rows.filter((r) => (!from || r.date >= from) && (!to || r.date <= to));
function rowsFor(def, bundles) {
    if (def.source === 'referrals')
        return referralRows(bundles);
    if (def.source === 'enrolments')
        return enrolmentRows(bundles, def.programme ?? '');
    return formRows(bundles, def.forms ?? [], def.date ? { date: def.date } : {});
}
/** A measure over a period (from/to inclusive, YYYY-MM-DD; null = open). */
export function computeMeasure(def, bundles, from, to, cache) {
    const key = `${def.source ?? 'forms'}|${(def.forms ?? []).join(',')}|${def.programme ?? ''}|${def.date ?? ''}`;
    let all = cache?.get(key);
    if (!all) {
        all = rowsFor(def, bundles);
        cache?.set(key, all);
    }
    let rows = inPeriod(all, from, to);
    if (def.latest_of) {
        const latest = new Map();
        for (const r of rows) {
            const v = r.values[def.latest_of];
            if (v === null || v === undefined)
                continue;
            const prev = latest.get(r.patient.id);
            if (!prev || prev.date <= r.date)
                latest.set(r.patient.id, r);
        }
        rows = [...latest.values()];
    }
    if (def.where)
        rows = rows.filter((r) => ev(def.where, r.values) === true);
    if (def.group) {
        const counts = new Map();
        for (const r of rows) {
            const v = r.values[def.group];
            if (v === null || v === undefined || v === '')
                continue;
            const keys = String(v)
                .split(',')
                .filter(Boolean)
                .map((k) => def.group_map?.[k] ?? k);
            // one patient-visit counts once per group, even with two diagnoses of the same group
            for (const k of new Set(keys)) {
                if (def.distinct === 'patient') {
                    const s = counts.get(k) ?? new Set();
                    s.add(r.patient.id);
                    counts.set(k, s);
                }
                else
                    counts.set(k, (counts.get(k) ?? 0) + 1);
            }
        }
        const groups = [...counts.entries()]
            .map(([k, v]) => [k, typeof v === 'number' ? v : v.size])
            .sort((a, b) => b[1] - a[1]);
        return { id: def.id, count: groups.reduce((s, [, n]) => s + n, 0), groups };
    }
    const count = def.distinct === 'patient' ? new Set(rows.map((r) => r.patient.id)).size : rows.length;
    return { id: def.id, count };
}
/** A tile's figure: a count, a percentage of another measure, or a rate per N. */
export function tileFigure(tile, results) {
    const num = results.get(tile.value)?.count ?? 0;
    if (!tile.of)
        return { value: num, num, den: null };
    const den = results.get(tile.of)?.count ?? 0;
    return { value: den ? Math.round(((tile.per ?? 100) * num) / den) : null, num, den };
}
/**
 * A baseline-aware flag for the latest month of a series: at least 4 points and 3 non-zero earlier
 * months, then beyond 2 standard deviations of the earlier months ('up' or 'down').
 */
export function anomaly(series) {
    const v = series.filter((x) => x !== null);
    if (v.length < 4)
        return null;
    const prior = v.slice(0, -1);
    const last = v[v.length - 1];
    if (prior.filter((x) => x > 0).length < 3)
        return null;
    const m = prior.reduce((s, x) => s + x, 0) / prior.length;
    const sd = Math.sqrt(prior.reduce((s, x) => s + (x - m) ** 2, 0) / prior.length);
    if (!(sd > 0))
        return null;
    if (last > m + 2 * sd)
        return 'up';
    if (last < m - 2 * sd)
        return 'down';
    return null;
}
/** A register's rows over a period, each cell evaluated (raw values; the screen formats them). */
export function registerRows(def, bundles, from, to) {
    const every = formRows(bundles, def.forms, {
        ...(def.person ? { person: def.person } : {}),
        ...(def.date ? { date: def.date } : {}),
        ...(def.first ? { first: def.first } : {}),
    });
    const all = def.where ? every.filter((r) => ev(def.where, r.values) === true) : every;
    const base = inPeriod(def.enter ? all.filter((r) => def.enter?.includes(String(r.values.form))) : all, from, to);
    let rows = base;
    if (def.row === 'patient') {
        const who = new Map();
        for (const r of base)
            who.set(r.patient.id, r.patient);
        rows = [...who.values()].map((p) => {
            const mine = all.filter((r) => r.patient.id === p.id);
            const values = {};
            for (const r of mine)
                for (const [k, v] of Object.entries(r.values))
                    if (v !== null && v !== undefined)
                        values[k] = v;
            if (def.doses)
                for (const r of mine) {
                    const n = r.values[def.doses];
                    if (n !== null && n !== undefined)
                        values[`dose_${n}`] = r.date;
                }
            const first = mine[0];
            return { patient: p, date: first.date, values: { ...values, date: first.date } };
        });
    }
    if (def.babies) {
        const babyRows = formRows(bundles, def.babies, def.first ? { first: def.first } : {});
        rows = rows.flatMap((r) => {
            const babies = bundles.filter((b) => motherOf(b.patient) === r.patient.id &&
                (def.row === 'patient'
                    ? // a person register (PMTCT): only the babies the register is about (an HEI record)
                        babyRows.some((x) => x.patient.id === b.patient.id)
                    : !!b.patient.birthDate && Math.abs(daysBetween(b.patient.birthDate, r.date)) <= 2));
            if (!babies.length)
                return [r];
            return babies.map((b) => {
                const values = {};
                for (const x of babyRows.filter((y) => y.patient.id === b.patient.id))
                    for (const [k, v] of Object.entries(x.values))
                        if (v !== null && v !== undefined)
                            values[k] = v;
                const baby = {
                    baby_sex: b.patient.gender ?? null,
                    baby_mrn: b.patient.identifier?.find((i) => i.system === SYS_FACILITY_MRN)?.value ?? null,
                    baby_dob: b.patient.birthDate ?? null,
                };
                return { ...r, values: { ...r.values, ...values, ...baby, ...personOnly(r.values) } };
            });
        });
    }
    if (def.tally) {
        const t = def.tally;
        const groups = new Map();
        for (const r of rows) {
            const k = String(ev(t.by, r.values) ?? 'unknown');
            groups.set(k, [...(groups.get(k) ?? []), r.values]);
        }
        return [...groups.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([k, rs]) => t.columns.map((c) => c.label ? k : rs.filter((r) => !c.count || ev(c.count, r) === true).length));
    }
    return rows.map((r, i) => def.columns.map((c) => (c.v === 'row' ? i + 1 : ev(c.v, r.values))));
}
// On a row joined with her baby's, the mother's identity and the row's date stay hers.
const PERSON_KEYS = [
    'name',
    'mrn',
    'kebele',
    'woreda',
    'phone',
    'sex',
    'dob',
    'age',
    'age_days',
    'date',
];
const personOnly = (v) => Object.fromEntries(PERSON_KEYS.map((k) => [k, v[k] ?? null]));
//# sourceMappingURL=report.js.map